/**
 * Chart config — attribute normalisation for the editor preview.
 *
 * Mirrors Chart\ChartData::normalize() in PHP. The editor and the frontend read
 * the same shape, so the preview and the published page cannot disagree about
 * what a set of attributes means.
 */

import { CHROME, MAX_SERIES, PALETTES, seriesColor } from './palettes';
import { resolveScale } from './geometry';
import { resolvePresetValue } from '../../../utils/helpers';

export const RADIAL_TYPES = [ 'pie', 'donut' ];
export const CHART_TYPES = [ 'bar', 'line', 'area', 'pie', 'donut' ];

export { MAX_SERIES };

/**
 * Normalise block attributes into the shape the preview components expect.
 *
 * @param {Object} attributes Block attributes.
 * @return {Object} Normalised config.
 */
export function normalizeConfig( attributes ) {
	const type = CHART_TYPES.includes( attributes.chartType )
		? attributes.chartType
		: 'bar';
	const isRadial = RADIAL_TYPES.includes( type );

	const categories = ( attributes.categories || [] ).map( ( category ) =>
		category === null || category === undefined ? '' : String( category )
	);

	const paletteName = PALETTES[ attributes.paletteName ]
		? attributes.paletteName
		: 'light';

	let series = normalizeSeries(
		attributes.series || [],
		categories.length,
		paletteName
	);

	// A radial chart plots one series; extra series have no wedge to occupy.
	if ( isRadial ) {
		series = series.slice( 0, 1 );
		series[ 0 ] = {
			...series[ 0 ],
			sliceColors: categories.map(
				( _, index ) =>
					PALETTES[ paletteName ][ index % MAX_SERIES ]
			),
		};
	}

	const stacked =
		!! attributes.stacked && [ 'bar', 'area' ].includes( type );

	const config = {
		type,
		isRadial,
		horizontal:
			! isRadial &&
			'bar' === type &&
			'horizontal' === attributes.orientation,
		stacked,
		categories,
		series,
		palette: paletteName,
		chrome: CHROME[ paletteName ],
		surface: resolvePresetValue( attributes.surfaceColor || '' ),
		plotHeight: Math.max( 120, attributes.plotHeight ?? 320 ),
		barMax: Math.max( 4, attributes.barMaxThickness ?? 24 ),
		donutRatio:
			'donut' === type
				? Math.min(
						0.9,
						Math.max( 0.1, ( attributes.donutHole ?? 55 ) / 100 )
				  )
				: 0,
		showGrid: !! attributes.showGrid,
		showValue: !! attributes.showValueAxis,
		showCategory: !! attributes.showCategoryAxis,
		rotate: !! attributes.rotateCategoryLabels,
		valueLabels: [ 'none', 'selective', 'all' ].includes(
			attributes.valueLabels
		)
			? attributes.valueLabels
			: 'selective',
		smooth: !! attributes.smoothLine,
		markers: !! attributes.showMarkers,
		tooltip: !! attributes.showTooltip,
		table: !! attributes.showDataTable,
		tableLabel: attributes.dataTableLabel ?? 'Show data',
		categoryHead: attributes.categoryHeading ?? 'Category',
		label: attributes.chartLabel ?? '',
		format: {
			decimals: Math.max( 0, attributes.decimals ?? 0 ),
			thousandSeparator: !! attributes.thousandSeparator,
			valuePrefix: attributes.valuePrefix ?? '',
			valueSuffix: attributes.valueSuffix ?? '',
		},
	};

	// A legend for one series just restates the heading above the chart.
	config.showLegend =
		!! attributes.showLegend &&
		( isRadial ? categories.length > 1 : series.length > 1 );
	config.legendPosition =
		'top' === attributes.legendPosition ? 'top' : 'bottom';

	if ( ! isRadial ) {
		config.scale = resolveScale( series, {
			stacked,
			// Only a plain line may float off zero; a filled mark cannot.
			zeroBased: 'line' !== type || stacked,
			tickCount: Math.max( 2, attributes.tickCount ?? 5 ),
			axisMin: attributes.axisMin || null,
			axisMax: attributes.axisMax || null,
		} );
	}

	return config;
}

/**
 * Normalise series, padding every data row to the category count.
 *
 * A short row would otherwise leave a band with no mark and shift the ones after
 * it, which reads as data rather than as a missing cell.
 *
 * @param {Array}  series      Raw series.
 * @param {number} count       Category count.
 * @param {string} paletteName Palette key.
 * @return {Array} Normalised series.
 */
function normalizeSeries( series, count, paletteName ) {
	const normalized = series
		.filter( ( one ) => one && typeof one === 'object' )
		.map( ( one, index ) => ( {
			label: one.label ?? '',
			color: seriesColor( one, index, paletteName ),
			data: Array.from( { length: count }, ( _, i ) => {
				const value = ( one.data || [] )[ i ];
				return typeof value === 'number' && isFinite( value )
					? value
					: parseFloat( value ) || 0;
			} ),
		} ) );

	if ( ! normalized.length ) {
		normalized.push( {
			label: '',
			color: PALETTES[ paletteName ][ 0 ],
			data: new Array( Math.max( 1, count ) ).fill( 0 ),
		} );
	}

	return normalized;
}

/**
 * The CSS custom properties the stylesheet reads.
 *
 * Mirrors ChartRenderer::style_vars().
 *
 * @param {Object} config Normalised config.
 * @return {Object} React style object.
 */
export function styleVars( config ) {
	return {
		'--chart-surface': config.surface || config.chrome.surface,
		'--chart-grid': config.chrome.grid,
		'--chart-axis': config.chrome.axis,
		'--chart-ink': config.chrome.ink,
		'--chart-muted': config.chrome.muted,
		'--chart-bar-max': `${ config.barMax }px`,
		'--chart-plot-height': `${ config.plotHeight }px`,
		// Horizontal bars stack their categories down the page instead of
		// across it, so there the category count costs no width at all.
		'--chart-min-width':
			config.horizontal || config.isRadial
				? '0px'
				: `${ config.categories.length * 44 }px`,
	};
}

/**
 * The root class list. Mirrors the classes render.php builds.
 *
 * @param {Object} config Normalised config.
 * @return {string} Class list.
 */
export function rootClassName( config ) {
	return [
		'brandy-chart',
		`brandy-chart--${ config.type }`,
		`brandy-chart--palette-${ config.palette }`,
		`brandy-chart--${ config.horizontal ? 'horizontal' : 'vertical' }`,
		config.stacked ? 'is-stacked' : '',
		config.tooltip ? 'has-tooltip' : '',
	]
		.filter( Boolean )
		.join( ' ' );
}
